import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface OnboardingState {
  showSitesOnboarding: boolean;
  showCheckpointsOnboarding: boolean;
  showGuardsOnboarding: boolean;
  showAllTips: boolean;
}

interface OnboardingContextType {
  state: OnboardingState;
  dismissOnboarding: (key: keyof Omit<OnboardingState, 'showAllTips'>) => void;
  toggleAllTips: () => void;
  resetOnboarding: () => void;
}

const STORAGE_KEY = 'patrolflow_onboarding';

const defaultState: OnboardingState = {
  showSitesOnboarding: true,
  showCheckpointsOnboarding: true,
  showGuardsOnboarding: true,
  showAllTips: true,
};

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OnboardingState>(() => {
    if (typeof window === 'undefined') return defaultState;
    
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return { ...defaultState, ...JSON.parse(stored) };
      } catch {
        return defaultState;
      }
    }
    return defaultState;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const dismissOnboarding = (key: keyof Omit<OnboardingState, 'showAllTips'>) => {
    setState(prev => ({ ...prev, [key]: false }));
  };

  const toggleAllTips = () => {
    setState(prev => ({
      ...prev,
      showAllTips: !prev.showAllTips,
    }));
  };

  const resetOnboarding = () => {
    setState(defaultState);
  };

  return (
    <OnboardingContext.Provider value={{ state, dismissOnboarding, toggleAllTips, resetOnboarding }}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
